import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  saveCurrentRoute,
  getLastRoute,
  clearRouteHistory,
} from '@services/routePersistenceService';
import { PublicRoutes } from '@enums/publicRouteEnums';
import { PrivateRoutes } from '@enums/privateRouteEnums';
import { LocalStorageKeyConstants } from '@constants/general/localStorageKeyConstants';

export const useRoutePersistence = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastIntentionalPath = useRef<string | null>(null);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(LocalStorageKeyConstants.IS_PAGE_REFRESH, 'true');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const saveRoute = async () => {
      const isPageRefresh =
        sessionStorage.getItem(LocalStorageKeyConstants.IS_PAGE_REFRESH) ===
        'true';

      if (
        isPageRefresh &&
        !location.pathname.includes(PublicRoutes.LOGIN) &&
        !location.pathname.includes(PublicRoutes.FORGOT_PASSWORD) &&
        !location.pathname.includes(PublicRoutes.RESET_PASSWORD) &&
        !location.pathname.includes(PublicRoutes.SERVICE_MAINTENANCE) &&
        !location.pathname
          .toLowerCase()
          .includes(PublicRoutes.DEFAULT.toLowerCase()) &&
        location.pathname !== PrivateRoutes.ROOT
      ) {
        await saveCurrentRoute(location.pathname);
      }

      sessionStorage.removeItem(LocalStorageKeyConstants.IS_PAGE_REFRESH);
    };

    saveRoute();
  }, [location.pathname]);

  const restoreLastRoute = useCallback(async () => {
    try {
      const lastRoute = await getLastRoute();
      if (lastRoute && lastRoute.path !== location.pathname) {
        if (lastRoute.path !== lastIntentionalPath.current) {
          navigate(lastRoute.path, { replace: true });
        }
      }
    } catch (error) {
      console.error('Failed to restore last route:', error);
    }
  }, [navigate, location.pathname]);

  const clearHistory = useCallback(() => {
    clearRouteHistory();
    lastIntentionalPath.current = null;
  }, []);

  const setIntentionalPath = useCallback((path: string) => {
    lastIntentionalPath.current = path;
  }, []);

  return {
    restoreLastRoute,
    clearHistory,
    setIntentionalPath,
  };
};
